import { Minus, Plus } from 'lucide-react'
import { useContext, useMemo } from 'react'
import { toast } from 'sonner'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { CopyButtonWithAnimationActionIcon } from 'components/reusable/CopyButtonWithAnimation'
import { Badge } from 'slab'
import { useGetBrokersMembersMemberIdCoveredParticipants } from 'utils/web_api'

type DependentChangesSectionProps = {
  dependentsAdded: string[]
  dependentsRemoved: string[]
  fallbackMemberId?: string
  headerVariant?: 'full' | 'compact'
}

const DependentChangesSection = ({
  dependentsAdded,
  dependentsRemoved,
  fallbackMemberId,
  headerVariant = 'full',
}: DependentChangesSectionProps) => {
  const { memberId } = useContext(BrokersMemberContext)

  const hasChanges = dependentsAdded.length > 0 || dependentsRemoved.length > 0

  // Fetch covered participants data
  const { data: participants } = useGetBrokersMembersMemberIdCoveredParticipants(
    memberId || fallbackMemberId || '',
    { include_pending: true },
    {
      query: {
        enabled: !!(memberId || fallbackMemberId) && hasChanges,
      },
    }
  )

  // Helper function to get participant display name by ID
  const getParticipantName = useMemo(() => {
    if (!participants) return (id: string) => `Participant ${id.slice(-6)}`

    const participantMap = new Map(
      participants.map((p) => [p.id, `${p.given_name} ${p.family_name}`])
    )

    return (id: string) => participantMap.get(id) || `Participant ${id.slice(-6)}`
  }, [participants])

  const headerText =
    headerVariant === 'full' ? 'Dependent Changes:' : 'Changes:'

  // Copy handler for participant IDs
  const handleCopyParticipantId = async (
    participantId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(participantId)
      toast.success('Participant ID copied to clipboard', {
        position: 'top-center',
      })
    } catch {
      toast.error('Failed to copy Participant ID', {
        position: 'top-center',
      })
    }
  }

  if (!hasChanges) {
    return null
  }

  return (
    <div className='space-y-2'>
      <h6 className='font-medium text-sm text-gray-600'>{headerText}</h6>
      <div className='flex flex-wrap gap-2'>
        {dependentsAdded.map((dependentId) => (
          <Badge
            key={`added-${dependentId}`}
            type='success'
            leftIcon={Plus}
            bordered
            actionIcon={CopyButtonWithAnimationActionIcon}
            actionLabel='Copy Participant ID'
            onActionClick={
              ((e: React.MouseEvent) =>
                handleCopyParticipantId(dependentId, e)) as any
            }
          >
            {getParticipantName(dependentId)}
          </Badge>
        ))}
        {dependentsRemoved.map((dependentId) => (
          <Badge
            key={`removed-${dependentId}`}
            type='critical'
            leftIcon={Minus}
            bordered
            actionIcon={CopyButtonWithAnimationActionIcon}
            actionLabel='Copy Participant ID'
            onActionClick={
              ((e: React.MouseEvent) =>
                handleCopyParticipantId(dependentId, e)) as any
            }
          >
            {getParticipantName(dependentId)}
          </Badge>
        ))}
      </div>
    </div>
  )
}

export default DependentChangesSection
